import { useCallback, useState } from "react";
import {
  createAuthChallenge,
  createSession,
  updateCurrentProfile,
  type Session,
} from "../lib/api";
import { ensureKukaiLogin, getKukaiEmbed, logoutKukaiEmbed } from "../lib/kukai";

const STORAGE_KEY = "club-drobbit-session";
const ONBOARDING_KEY = "club-drobbit-onboarding-address";

function loadStoredSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (parsed.address && parsed.displayName && parsed.token) {
      // Ensure avatarColor exists for sessions stored before this field was added
      if (!parsed.avatarColor) parsed.avatarColor = "#000000";
      if (typeof parsed.isAdmin !== "boolean") parsed.isAdmin = false;
      if (!parsed.tokenExpiresAt) return null;
      if (Date.parse(parsed.tokenExpiresAt) <= Date.now()) return null;
      return parsed;
    }
  } catch {
    // Corrupt data — ignore
  }
  return null;
}

function loadOnboardingAddress(): string | null {
  try {
    const raw = localStorage.getItem(ONBOARDING_KEY);
    return raw?.trim() || null;
  } catch {
    return null;
  }
}

function storeSession(session: Session | null) {
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

function storeOnboardingAddress(address: string | null) {
  if (address) {
    localStorage.setItem(ONBOARDING_KEY, address);
  } else {
    localStorage.removeItem(ONBOARDING_KEY);
  }
}

export function useSession() {
  const [session, setSession] = useState<Session | null>(loadStoredSession);
  const [loading, setLoading] = useState(false);
  const [onboardingAddress, setOnboardingAddress] = useState<string | null>(
    loadOnboardingAddress
  );

  const syncOnboardingAddress = useCallback(
    (address: string, isFirstLogin: boolean) => {
      setOnboardingAddress((prev) => {
        const next = isFirstLogin ? address : prev === address ? prev : null;
        storeOnboardingAddress(next);
        return next;
      });
    },
    []
  );

  const connectWithWallet = useCallback(async () => {
    setLoading(true);
    try {
      const beacon = await import("@airgap/beacon-sdk");
      let client: InstanceType<typeof beacon.DAppClient>;
      const baseClientConfig = {
        name: "Club Drobbit",
        network: { type: beacon.NetworkType.MAINNET },
      } as ConstructorParameters<typeof beacon.DAppClient>[0];
      try {
        client = new beacon.DAppClient({
          ...baseClientConfig,
          enableMetrics: false,
        } as ConstructorParameters<typeof beacon.DAppClient>[0]);
      } catch (e: unknown) {
        // Some Beacon SDK versions throw on init when metrics DB is unavailable
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes("metrics")) {
          client = new beacon.DAppClient(baseClientConfig);
        } else {
          throw e;
        }
      }
      await client.subscribeToEvent(beacon.BeaconEvent.ACTIVE_ACCOUNT_SET, () => {
        // Required in Beacon v4.2+ migration path.
      });
      try {
        await client.requestPermissions({
          scopes: [beacon.PermissionScope.OPERATION_REQUEST, beacon.PermissionScope.SIGN],
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        // Suppress metrics-related errors that don't affect wallet functionality
        if (!msg.includes("metrics")) throw e;
      }
      const account = await client.getActiveAccount();
      if (!account?.address) {
        throw new Error("Wallet did not return an address.");
      }
      const challenge = await createAuthChallenge(account.address);
      let signingType = beacon.SigningType.RAW;
      if (challenge.signingType === "micheline") {
        signingType = beacon.SigningType.MICHELINE;
      } else if (challenge.signingType === "operation") {
        signingType = beacon.SigningType.OPERATION;
      }
      const signResult = await client.requestSignPayload({
        signingType,
        payload: challenge.payloadHex,
      });
      const publicKey =
        account.publicKey || (signResult as { publicKey?: string }).publicKey;
      if (!publicKey) {
        throw new Error("Wallet did not return a public key.");
      }
      const result = await createSession(account.address, {
        challengeId: challenge.challengeId,
        signature: signResult.signature,
        publicKey,
      });
      const s = result.session;
      setSession(s);
      storeSession(s);
      syncOnboardingAddress(s.address, result.isFirstLogin);
      return result;
    } finally {
      setLoading(false);
    }
  }, [syncOnboardingAddress]);

  const connectWithKukai = useCallback(async () => {
    setLoading(true);
    try {
      const embed = await getKukaiEmbed();
      const login = await ensureKukaiLogin();
      const address = login?.pkh?.trim();
      const publicKey = login?.pk?.trim();
      if (!address || !publicKey) {
        throw new Error("Kukai did not return a valid account.");
      }
      const challenge = await createAuthChallenge(address);
      const signature = await embed.signExpr(challenge.payloadHex);
      const result = await createSession(address, {
        challengeId: challenge.challengeId,
        signature,
        publicKey,
      });
      const s = result.session;
      setSession(s);
      storeSession(s);
      syncOnboardingAddress(s.address, result.isFirstLogin);
      return result;
    } finally {
      setLoading(false);
    }
  }, [syncOnboardingAddress]);

  const connectWithDevAddress = useCallback(
    async (address: string) => {
      setLoading(true);
      try {
        const result = await createSession(address, { devBypass: true });
        const s = result.session;
        setSession(s);
        storeSession(s);
        syncOnboardingAddress(s.address, result.isFirstLogin);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [syncOnboardingAddress]
  );

  const disconnect = useCallback(() => {
    void logoutKukaiEmbed();
    setSession(null);
    storeSession(null);
  }, []);

  const setAvatarColor = useCallback((color: string) => {
    setSession((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, avatarColor: color };
      storeSession(updated);
      return updated;
    });
  }, []);

  const setDisplayName = useCallback(
    async (name: string) => {
      setSession((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, displayName: name };
        storeSession(updated);
        return updated;
      });
      // Persist to server
      if (session) {
        await updateCurrentProfile(session, { displayName: name }).catch(() => {});
      }
    },
    [session]
  );

  const completeOnboarding = useCallback(() => {
    if (!session?.address) return;
    setOnboardingAddress((prev) => {
      if (prev !== session.address) return prev;
      storeOnboardingAddress(null);
      return null;
    });
  }, [session?.address]);

  const needsOnboarding = Boolean(
    session?.address && onboardingAddress === session.address
  );

  return {
    session,
    loading,
    connectWithKukai,
    connectWithWallet,
    connectWithDevAddress,
    disconnect,
    setAvatarColor,
    setDisplayName,
    needsOnboarding,
    completeOnboarding,
  };
}
